import type { GoalFlowTier } from "@/components/cinch/GoalFlowStory";

import type { GoalSummary } from "../api";

/**
 * Per-year "flow story" layout for the Goals page.
 *
 * Human decision (see plan): the *visual* framing of the goals is expected to
 * change every year, so the layout lives in the frontend as a per-year config
 * rather than in the DB. The editable goal *content* (titles, descriptions,
 * success criteria) comes from the API; this file only decides how the year's
 * goals are arranged into the narrative and what slide-specific copy each box
 * shows. Add a new `STORY_<year>` + branch in `buildStoryTiers` each year.
 *
 * FUTURE: if framing should be editable without a deploy, move this into a
 * `goals.goal_years.narrative` JSONB column and render from that instead.
 */

interface StoryBoxSpec {
  /** References Goal.key — resolves to the goal's id (click target) + accent. */
  key: string;
  /** Slide-specific box copy (differs from the goal's own title/tagline). */
  title: string;
  subtitle?: string;
}

interface StoryTierSpec {
  label: string;
  boxes: StoryBoxSpec[];
}

// 2026 — the "Make It Easy" story from the planning deck.
const STORY_2026: StoryTierSpec[] = [
  {
    label: "If we make it easier to access",
    boxes: [
      { key: "plg", title: "Product led growth", subtitle: "Self-serve onboarding" },
      { key: "integrations", title: "Faster integrations", subtitle: "Cinch connected" },
    ],
  },
  {
    label: "& easier to use",
    boxes: [
      { key: "ai_adoption", title: "Cinch AI", subtitle: "More value from Cinch with less effort" },
    ],
  },
  {
    label: "Then it's easier to sell",
    boxes: [
      {
        key: "top_of_funnel",
        title: "Top of funnel grows",
        subtitle: "Our story sells itself when the product delivers value fast",
      },
    ],
  },
];

const STORIES: Record<number, StoryTierSpec[]> = {
  2026: STORY_2026,
};

/**
 * Resolve a year's story spec against the goals loaded from the API. Boxes
 * reference goals by `key`; missing keys are dropped (e.g. a goal was deleted),
 * empty tiers are removed. Years without a bespoke story fall back to a single
 * tier listing every goal — so the page degrades gracefully until next year's
 * layout is authored.
 */
export function buildStoryTiers(year: number, goals: GoalSummary[]): GoalFlowTier[] {
  const byKey = new Map(goals.map((g) => [g.key, g]));
  const spec = STORIES[year];

  if (!spec) {
    return [
      {
        label: `${year} goals`,
        boxes: goals.map((g) => ({
          id: g.id,
          title: g.short_label || g.title,
          subtitle: g.tagline ?? undefined,
          accent: g.accent,
        })),
      },
    ];
  }

  return spec
    .map((tier) => ({
      label: tier.label,
      boxes: tier.boxes.flatMap((b) => {
        const g = byKey.get(b.key);
        if (!g) return [];
        return [{ id: g.id, title: b.title, subtitle: b.subtitle, accent: g.accent }];
      }),
    }))
    .filter((tier) => tier.boxes.length > 0);
}
